/**
 * ComentariosHilo — conversación de un hilo (general del proyecto o de una tarea).
 * Lo usan el Portal de Clientes (modo "cliente") y el admin (modo "admin").
 *
 * - Carga desde Supabase; quién ve qué lo decide el RLS (el cliente nunca
 *   recibe notas internas, aunque este componente las sabe pintar).
 * - Mensajes nuevos por Realtime; si el canal no está disponible, consulta
 *   cada 20 s mientras la pestaña esté visible.
 * - Envío idempotente: un client_request_id por mensaje evita duplicados ante
 *   doble clic o reintentos.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { Loader, Send, RefreshCw, AlertCircle, Lock, MessageSquare } from "lucide-react";
import {
  listarComentarios, enviarComentario, marcarLectura, suscribirComentarios,
  nuevoRequestId, mensajeError, hiloDe,
} from "../../lib/comentarios";
import { fmtFechaHora } from "../../lib/formato";

const C = {
  bg:"#f8fafc", white:"#ffffff", border:"#e2e8f0",
  accent:"#29abe2", accentL:"#e8f5fc",
  text:"#1e293b", sub:"#64748b", light:"#94a3b8",
  ok:"#10b981", okL:"#d1fae5",
  err:"#ef4444", errL:"#fee2e2",
  warn:"#f59e0b", warnL:"#fef3c7",
  int:"#a16207", intL:"#fefce8", intB:"#fde68a",
};

const MAX = 5000;
const POLL_MS = 20000;

export default function ComentariosHilo({
  proyectoId,
  tareaId = null,          // null = comentarios generales del proyecto
  modo = "cliente",        // "cliente" | "admin"
  titulo,
  placeholder,
  alturaMax = 420,
  onActividad,             // se llama al leer/enviar/recibir (para refrescar contadores)
}) {
  const [items,     setItems]     = useState([]);
  const [cargando,  setCargando]  = useState(true);
  const [errCarga,  setErrCarga]  = useState("");
  const [texto,     setTexto]     = useState("");
  const [interno,   setInterno]   = useState(false);
  const [enviando,  setEnviando]  = useState(false);
  const [errEnvio,  setErrEnvio]  = useState("");
  const [leidoAntes,setLeidoAntes]= useState(null);   // marca previa → separador "Nuevos"
  const [enVivo,    setEnVivo]    = useState(false);
  const [aviso,     setAviso]     = useState("");     // para lectores de pantalla

  const requestId = useRef(nuevoRequestId());
  const listaRef  = useRef(null);
  const montado   = useRef(true);
  const itemsRef  = useRef([]);
  const onActRef  = useRef(onActividad);
  onActRef.current = onActividad;
  const hilo      = hiloDe(tareaId);
  const idTexto   = `coment-${proyectoId}-${hilo}`;

  useEffect(() => () => { montado.current = false; }, []);

  const marcarLeido = useCallback(async () => {
    try {
      const prev = await marcarLectura(proyectoId, hilo);
      onActRef.current?.();
      return prev;
    } catch { return undefined; }    // no bloquea la lectura si falla
  }, [proyectoId, hilo]);

  const cargar = useCallback(async ({ silencioso = false } = {}) => {
    if (!silencioso) { setCargando(true); setErrCarga(""); }
    try {
      const data = await listarComentarios({ proyectoId, tareaId: tareaId || null });
      if (!montado.current) return;
      const n = data.length - itemsRef.current.length;
      if (silencioso && n > 0) setAviso(`${n} comentario${n > 1 ? "s" : ""} nuevo${n > 1 ? "s" : ""}`);
      itemsRef.current = data;
      setItems(data);
      setErrCarga("");
    } catch (e) {
      if (montado.current && !silencioso) setErrCarga(mensajeError(e));
    } finally {
      if (montado.current && !silencioso) setCargando(false);
    }
  }, [proyectoId, tareaId]);

  // Carga inicial + marca de lectura (guardamos la marca anterior para
  // señalar qué mensajes son nuevos para este usuario).
  useEffect(() => {
    let vivo = true;
    (async () => {
      await cargar();
      const prev = await marcarLeido();
      if (vivo) setLeidoAntes(prev === undefined ? null : prev);
    })();
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proyectoId, tareaId]);

  // Realtime con respaldo por consulta periódica
  useEffect(() => {
    const cancelar = suscribirComentarios(proyectoId, {
      onInsert: (fila) => {
        if ((fila.tarea_id || null) !== (tareaId || null)) return;
        // El payload de Realtime ya viene filtrado por RLS; recargamos el hilo
        // para obtener la fila con el mismo formato y orden que la consulta.
        cargar({ silencioso: true });
        if (document.visibilityState === "visible") marcarLeido();
      },
      onEstado: (estado) => setEnVivo(estado === "SUBSCRIBED"),
    });
    return cancelar;
  }, [proyectoId, tareaId, cargar, marcarLeido]);

  useEffect(() => {
    if (enVivo) return undefined;
    const t = setInterval(() => {
      if (document.visibilityState === "visible") cargar({ silencioso: true });
    }, POLL_MS);
    return () => clearInterval(t);
  }, [enVivo, cargar]);

  // Desplazar al último mensaje cuando llegan nuevos
  useEffect(() => {
    const el = listaRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [items.length]);

  async function enviar(e) {
    e?.preventDefault();
    const limpio = texto.trim();
    if (!limpio || enviando) return;
    if (limpio.length > MAX) { setErrEnvio(`Máximo ${MAX} caracteres.`); return; }
    setEnviando(true); setErrEnvio("");
    try {
      const nuevo = await enviarComentario({
        proyectoId, tareaId, comentario: limpio,
        esInterno: modo === "admin" && interno,
        requestId: requestId.current,
      });
      if (!itemsRef.current.some(x => x.id === nuevo.id)) {
        itemsRef.current = [...itemsRef.current, nuevo];
        setItems(itemsRef.current);
      }
      setTexto(""); setInterno(false);
      requestId.current = nuevoRequestId();   // solo tras confirmación del servidor
      setAviso("Comentario enviado");
      marcarLeido();
    } catch (err) {
      setErrEnvio(mensajeError(err));          // el texto se conserva para reintentar
    } finally {
      setEnviando(false);
    }
  }

  const propioTipo = modo === "admin" ? "equipo" : "cliente";
  const idxNuevo = leidoAntes === null
    ? -1
    : items.findIndex(x => new Date(x.created_at) > new Date(leidoAntes) && x.tipo_autor !== propioTipo);
  const vacio = !cargando && !errCarga && items.length === 0;
  const puedeEnviar = !!texto.trim() && !enviando && texto.trim().length <= MAX;

  return (
    <section aria-label={titulo || "Comentarios"} style={{display:"flex", flexDirection:"column", gap:10}}>
      {titulo && (
        <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", gap:8}}>
          <h3 style={{margin:0, fontSize:14, fontWeight:700, color:C.text,
            display:"flex", alignItems:"center", gap:7}}>
            <MessageSquare size={15} color={C.accent} aria-hidden="true"/> {titulo}
          </h3>
          <button type="button" onClick={() => cargar()} aria-label="Actualizar conversación"
            title={enVivo ? "Actualización en vivo activa" : "Actualizar"}
            style={btnIcono}>
            <RefreshCw size={13} aria-hidden="true"/>
            <span style={{fontSize:11}}>{enVivo ? "En vivo" : "Actualizar"}</span>
          </button>
        </div>
      )}

      <div aria-live="polite" style={srOnly}>{aviso}</div>

      {/* Lista */}
      <div ref={listaRef} role="log" aria-label="Historial de comentarios"
        style={{maxHeight:alturaMax, overflowY:"auto", display:"flex", flexDirection:"column",
          gap:10, padding:"4px 2px"}}>
        {cargando && (
          <div style={{padding:18, textAlign:"center", color:C.sub, fontSize:13,
            display:"flex", alignItems:"center", justifyContent:"center", gap:8}}>
            <Loader size={14} className="spinning" aria-hidden="true"/> Cargando comentarios...
          </div>
        )}

        {errCarga && (
          <div role="alert" style={cajaError}>
            <AlertCircle size={14} aria-hidden="true"/> <span style={{flex:1}}>{errCarga}</span>
            <button type="button" onClick={() => cargar()} style={{...btnIcono, color:C.err}}>Reintentar</button>
          </div>
        )}

        {vacio && (
          <div style={{padding:"22px 14px", textAlign:"center", color:C.sub, fontSize:13,
            background:C.bg, border:`1px dashed ${C.border}`, borderRadius:10}}>
            {modo === "admin"
              ? "Aún no hay comentarios en esta conversación."
              : "Aún no hay comentarios. Escribe tu consulta y el equipo de TuAplicacion te responderá aquí."}
          </div>
        )}

        {!cargando && items.map((c, i) => (
          <div key={c.id}>
            {i === idxNuevo && (
              <div role="separator" aria-label="Comentarios nuevos"
                style={{display:"flex", alignItems:"center", gap:8, margin:"2px 0 8px",
                  color:C.accent, fontSize:11, fontWeight:700}}>
                <span style={{flex:1, height:1, background:`${C.accent}50`}}/> NUEVOS
                <span style={{flex:1, height:1, background:`${C.accent}50`}}/>
              </div>
            )}
            <Burbuja c={c} propio={c.tipo_autor === propioTipo}/>
          </div>
        ))}
      </div>

      {/* Formulario */}
      <form onSubmit={enviar} style={{display:"flex", flexDirection:"column", gap:8}}>
        <label htmlFor={idTexto} style={srOnly}>
          {modo === "admin" ? "Escribir respuesta" : "Escribir comentario"}
        </label>
        <textarea id={idTexto} value={texto} rows={3} maxLength={MAX + 100}
          onChange={e => { setTexto(e.target.value); if (errEnvio) setErrEnvio(""); }}
          onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) enviar(e); }}
          placeholder={placeholder || (modo === "admin"
            ? "Responder al cliente… (Ctrl+Enter para enviar)"
            : "Escribe tu comentario o consulta… (Ctrl+Enter para enviar)")}
          disabled={enviando}
          aria-invalid={!!errEnvio}
          aria-describedby={errEnvio ? `${idTexto}-err` : undefined}
          style={{width:"100%", padding:"10px 12px", borderRadius:8, resize:"vertical",
            border:`1px solid ${interno ? C.intB : C.border}`,
            background: interno ? C.intL : C.white,
            fontSize:14, color:C.text, fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box"}}/>

        {errEnvio && (
          <div id={`${idTexto}-err`} role="alert" style={cajaError}>
            <AlertCircle size={14} aria-hidden="true"/> {errEnvio}
          </div>
        )}

        <div style={{display:"flex", alignItems:"center", justifyContent:"space-between",
          gap:10, flexWrap:"wrap"}}>
          {modo === "admin" ? (
            <label style={{display:"flex", alignItems:"center", gap:6, fontSize:12,
              color: interno ? C.int : C.sub, cursor:"pointer", fontWeight: interno ? 700 : 500}}>
              <input type="checkbox" checked={interno} onChange={e => setInterno(e.target.checked)}/>
              <Lock size={12} aria-hidden="true"/> Nota interna (solo equipo, no se notifica)
            </label>
          ) : (
            <span style={{fontSize:11, color:C.light}}>
              {texto.length > MAX * 0.8 ? `${texto.trim().length}/${MAX}` : ""}
            </span>
          )}
          <button type="submit" disabled={!puedeEnviar}
            style={{display:"flex", alignItems:"center", gap:6, padding:"8px 18px",
              borderRadius:8, border:"none", fontWeight:700, fontSize:13,
              fontFamily:"'DM Sans',sans-serif",
              background: !puedeEnviar ? C.light : interno ? C.int : C.accent,
              color:"#fff", cursor: !puedeEnviar ? "not-allowed" : "pointer"}}>
            {enviando
              ? <><Loader size={13} className="spinning" aria-hidden="true"/> Enviando...</>
              : <><Send size={13} aria-hidden="true"/> {interno ? "Guardar nota" : "Enviar"}</>}
          </button>
        </div>
      </form>
    </section>
  );
}

function Burbuja({ c, propio }) {
  const esEquipo = c.tipo_autor === "equipo";
  const estilo = c.es_interno
    ? { bg:C.intL, borde:C.intB, etiqueta:"Nota interna", color:C.int }
    : esEquipo
      ? { bg:C.accentL, borde:`${C.accent}40`, etiqueta:"TuAplicacion", color:C.accent }
      : { bg:C.white, borde:C.border, etiqueta:"Cliente", color:C.sub };
  return (
    <article
      aria-label={`${estilo.etiqueta}: ${c.autor_nombre}, ${fmtFechaHora(c.created_at)}`}
      style={{alignSelf: propio ? "flex-end" : "flex-start", maxWidth:"88%",
        marginLeft: propio ? "auto" : 0,
        background:estilo.bg, border:`1px solid ${estilo.borde}`,
        borderRadius: propio ? "12px 12px 4px 12px" : "12px 12px 12px 4px",
        padding:"9px 12px", boxShadow:"0 1px 2px #0000000a"}}>
      <header style={{display:"flex", alignItems:"center", gap:6, flexWrap:"wrap", marginBottom:4}}>
        <span style={{fontWeight:700, fontSize:12, color:C.text}}>{c.autor_nombre}</span>
        <span style={{fontSize:10, fontWeight:700, color:estilo.color,
          padding:"1px 6px", borderRadius:4, background:"#ffffffa0",
          border:`1px solid ${estilo.borde}`, display:"inline-flex", alignItems:"center", gap:3}}>
          {c.es_interno && <Lock size={9} aria-hidden="true"/>}{estilo.etiqueta}
        </span>
        <time dateTime={c.created_at} style={{fontSize:11, color:C.light}}>
          {fmtFechaHora(c.created_at)}
        </time>
      </header>
      {c.tarea_id === null && c.tarea_descripcion && (
        <div style={{fontSize:11, color:C.light, marginBottom:3}}>
          Sobre una tarea eliminada: {c.tarea_descripcion}
        </div>
      )}
      <p style={{margin:0, fontSize:13, color:C.text, lineHeight:1.55,
        whiteSpace:"pre-wrap", overflowWrap:"anywhere"}}>
        {c.comentario}
      </p>
    </article>
  );
}

const srOnly = {
  position:"absolute", width:1, height:1, padding:0, margin:-1, overflow:"hidden",
  clip:"rect(0,0,0,0)", whiteSpace:"nowrap", border:0,
};
const btnIcono = {
  display:"flex", alignItems:"center", gap:4, padding:"4px 9px", borderRadius:6,
  border:`1px solid ${C.border}`, background:C.white, color:C.sub, cursor:"pointer",
  fontFamily:"'DM Sans',sans-serif", fontSize:12,
};
const cajaError = {
  display:"flex", alignItems:"center", gap:8, padding:"8px 12px",
  background:C.errL, border:`1px solid ${C.err}30`, borderRadius:8,
  color:C.err, fontSize:12, fontWeight:500,
};
